/**
 * Library Tool → SDK Function Tool conversion.
 *
 * Converts LibraryToolDefinition entries into SDK-native function tools
 * that each adapter can register with its respective LLM SDK.
 *
 * Each adapter receives tools in its native format:
 *   - Claude Agent SDK: MCP tool via createSdkMcpServer + tool()
 *   - OpenAI Agents SDK: function tool object
 *   - Google ADK: FunctionDeclaration tool
 */

import type { LibraryToolRegistry } from "./library-tool-binding.js";
import type { ObserverEmitFn } from "./observability-bridge.js";
import { emitToolExecClose } from "./observability-bridge.js";
import {
  buildAnalyzerCloseAttributes,
  ContextPackAdoptionTracker,
  isAnalyzerObservabilityTool,
} from "./context-pack-adoption.js";
import { ToolExecSpanRegistry } from "./tool-exec-span-registry.js";

/** Observability context for MCP handler completion-time tool_exec close (#151). */
export interface McpObservabilityContext {
  observer?: ObserverEmitFn;
  sessionId?: string;
  spanRegistry?: ToolExecSpanRegistry;
  adoptionTracker?: ContextPackAdoptionTracker;
}

/**
 * Adapter-agnostic function tool descriptor. Adapters convert this
 * into their SDK-specific tool format.
 */
export interface SdkFunctionTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  handler: (args: Record<string, unknown>) => Promise<string>;
}

/**
 * Build adapter-agnostic function tool descriptors from a LibraryToolRegistry.
 * Each tool's handler calls registry.execute() and serialises the result.
 */
export function buildSdkFunctionTools(
  registry: LibraryToolRegistry,
): SdkFunctionTool[] {
  return registry.getAll().map((def) => ({
    name: def.toolId,
    description: def.description,
    inputSchema: def.inputSchema ?? { type: "object", properties: {} },
    handler: async (args: Record<string, unknown>): Promise<string> => {
      const result = await registry.execute(def.toolId, args);
      return typeof result === "string" ? result : JSON.stringify(result);
    },
  }));
}

// ---------------------------------------------------------------------------
// Claude Agent SDK — MCP server wrapping
// ---------------------------------------------------------------------------

/**
 * Build Claude MCP server tools from a LibraryToolRegistry.
 *
 * Dynamically imports `tool` and `createSdkMcpServer` from the
 * Claude Agent SDK. Returns an MCP server instance to be passed
 * to query() via options.mcpServers.
 *
 * Returns undefined if no tools or if SDK functions are unavailable.
 */
export async function buildClaudeMcpServer(
  registry: LibraryToolRegistry,
  observability?: McpObservabilityContext,
): Promise<unknown | undefined> {
  const tools = buildSdkFunctionTools(registry);
  if (tools.length === 0) return undefined;

  const sdk = (await import("@anthropic-ai/claude-agent-sdk")) as Record<
    string,
    unknown
  >;
  type McpToolFn = (
    name: string,
    description: string,
    inputSchema: Record<string, unknown>,
    handler: (
      args: Record<string, unknown>,
      extra: unknown,
    ) => Promise<{ content: Array<{ type: string; text: string }> }>,
  ) => unknown;

  const toolFn = sdk.tool as McpToolFn | undefined;
  const createServer = sdk.createSdkMcpServer as
    | ((options: {
        name: string;
        version?: string;
        tools?: unknown[];
      }) => unknown)
    | undefined;

  if (!toolFn || !createServer) return undefined;

  const { z } = await import("zod");
  const { jsonSchemaToZod } = await import("./schema-converter.js");

  const sdkTools = tools.map((t) => {
    const zodType = jsonSchemaToZod(t.inputSchema);
    const shape =
      zodType instanceof z.ZodObject
        ? (zodType.shape as Record<string, unknown>)
        : {};

    return toolFn(
      t.name,
      t.description,
      shape,
      async (args) => {
        const startMs = Date.now();
        const text = await t.handler(
          (args ?? {}) as Record<string, unknown>,
        );
        if (observability?.observer && observability.spanRegistry) {
          const pending = observability.spanRegistry.closeFirst(t.name);
          if (pending) {
            const closeAttrs = isAnalyzerObservabilityTool(t.name)
              ? buildAnalyzerCloseAttributes(t.name, text)
              : { extraAttributes: {}, paths: [] as string[], contextPackId: undefined };
            emitToolExecClose(
              observability.observer,
              pending.spanId,
              t.name,
              Date.now() - startMs,
              {
                sessionId: observability.sessionId,
                extraAttributes: closeAttrs.extraAttributes,
                outputSummary: closeAttrs.extraAttributes["tool.output_summary"] as string | undefined,
              },
            );
            if (
              observability.adoptionTracker &&
              closeAttrs.contextPackId &&
              closeAttrs.paths.length > 0
            ) {
              observability.adoptionTracker.registerPack(
                observability.sessionId,
                pending.spanId,
                closeAttrs.contextPackId,
                closeAttrs.paths,
              );
            }
          }
        }
        return { content: [{ type: "text", text }] };
      },
    );
  });

  return createServer({
    name: "aaac-library-tools",
    version: "1.0.0",
    tools: sdkTools,
  });
}

// ---------------------------------------------------------------------------
// OpenAI Agents SDK — function tool objects
// ---------------------------------------------------------------------------

/**
 * Build OpenAI Agents SDK function tool objects from a LibraryToolRegistry.
 *
 * Dynamically imports `tool` from @openai/agents if available.
 * Falls back to a raw function-tool shape that the SDK also accepts.
 */
export async function buildOpenAIFunctionTools(
  registry: LibraryToolRegistry,
): Promise<unknown[]> {
  const tools = buildSdkFunctionTools(registry);
  if (tools.length === 0) return [];

  try {
    const sdk = (await import("@openai/agents")) as Record<string, unknown>;
    const toolFn = sdk.tool as
      | ((def: Record<string, unknown>) => unknown)
      | undefined;

    if (toolFn) {
      const { jsonSchemaToZod } = await import("./schema-converter.js");
      return tools.map((t) =>
        toolFn({
          name: t.name,
          description: t.description,
          parameters: jsonSchemaToZod(t.inputSchema),
          execute: async (_ctx: unknown, args: unknown) => {
            return t.handler(
              (args ?? {}) as Record<string, unknown>,
            );
          },
        }),
      );
    }
  } catch {
    // SDK not available; fall through to raw format
  }

  return tools.map((t) => ({
    type: "function" as const,
    function: {
      name: t.name,
      description: t.description,
      parameters: t.inputSchema,
    },
    execute: async (args: unknown) => {
      return t.handler(
        (args ?? {}) as Record<string, unknown>,
      );
    },
  }));
}

// ---------------------------------------------------------------------------
// Google ADK — function tool objects
// ---------------------------------------------------------------------------

/**
 * Build Google ADK function tool objects from a LibraryToolRegistry.
 *
 * ADK's LlmAgent accepts tools as objects with a FunctionDeclaration shape
 * or as callable tool instances. We produce lightweight callable wrappers.
 */
export async function buildAdkFunctionTools(
  registry: LibraryToolRegistry,
): Promise<unknown[]> {
  const tools = buildSdkFunctionTools(registry);
  if (tools.length === 0) return [];

  return tools.map((t) => ({
    name: t.name,
    description: t.description,
    inputSchema: t.inputSchema,
    execute: async (args: unknown) => {
      return t.handler(
        (args ?? {}) as Record<string, unknown>,
      );
    },
  }));
}
